import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import Logo from '../components/Logo';

// Grille de prix — l'avis d'un chef d'équipe, poste par poste, depuis son
// téléphone. Le lien porte un identifiant aléatoire ; la page montre le prix
// de grille tel qu'il sort sur le devis, et deux boutons : − si c'est trop
// cher, + si c'est trop bas, par pas de 5 %. Chaque appui s'enregistre.
// L'avis ne change pas la grille : il se lit dans l'espace Pro.
// Hors du Layout du site : pas de menu, pas de « devis gratuit ».

const fmt = (n) => new Intl.NumberFormat('fr-FR', { maximumFractionDigits: n < 100 ? 1 : 0 }).format(n || 0);
const UNITES = { 'm²': 'm²', ml: 'ml', u: 'unité', forfait: 'forfait', logement: 'logement', niveau: 'niveau', semaine: 'semaine', kW: 'kW' };

export default function GrillePrix() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [erreur, setErreur] = useState('');
  const [filtre, setFiltre] = useState('tous');
  const [recherche, setRecherche] = useState('');
  const [etat, setEtat] = useState({});   // cle → 'enregistre' | 'erreur'

  useEffect(() => {
    fetch(`/api/grille/${id}`)
      .then(async r => { if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || 'Lien inconnu'); return r.json(); })
      .then(setData)
      .catch(e => setErreur(e.message));
  }, [id]);

  // Un avis part au serveur après un court délai : trois appuis rapides sur +
  // n'envoient qu'une requête. L'écran, lui, change tout de suite.
  const timers = useRef({});
  const envoyer = (cle, avis) => {
    clearTimeout(timers.current[cle]);
    timers.current[cle] = setTimeout(async () => {
      try {
        const r = await fetch(`/api/grille/${id}/${cle}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ delta: avis.delta, commentaire: avis.commentaire }) });
        if (!r.ok) throw new Error();
        const { avis: a } = await r.json();
        setData(d => ({ ...d, lots: d.lots.map(l => ({ ...l, items: l.items.map(it => (it.cle === cle ? { ...it, avis: a } : it)) })) }));
        setEtat(s => ({ ...s, [cle]: 'enregistre' }));
        setTimeout(() => setEtat(s => { const n = { ...s }; delete n[cle]; return n; }), 1500);
      } catch { setEtat(s => ({ ...s, [cle]: 'erreur' })); }
    }, 600);
  };
  const modifier = (cle, patch) => {
    setData(d => ({
      ...d,
      lots: d.lots.map(l => ({
        ...l,
        items: l.items.map(it => {
          if (it.cle !== cle) return it;
          const avis = { delta: 0, commentaire: '', ...(it.avis || {}), ...patch };
          avis.delta = Math.max(-10, Math.min(20, avis.delta));
          avis.prix = it.prix * (1 + avis.delta * (d.pas || 0.05));
          envoyer(cle, avis);
          return { ...it, avis: (avis.delta === 0 && !avis.commentaire) ? null : avis };
        }),
      })),
    }));
  };

  const nbAvis = useMemo(() => (data?.lots || []).reduce((n, l) => n + l.items.filter(it => it.avis).length, 0), [data]);
  const q = recherche.trim().toLowerCase();
  const lots = useMemo(() => (data?.lots || []).map(l => ({
    ...l,
    items: l.items.filter(it => (filtre === 'avis' ? !!it.avis : true) && (!q || it.label.toLowerCase().includes(q))),
  })).filter(l => l.items.length), [data, filtre, q]);

  if (erreur) {
    return (
      <Ecran>
        <h1 className="text-xl font-bold text-slate-900">Lien inconnu</h1>
        <p className="mt-2 text-slate-600">Ce lien n'est pas (ou plus) valable. Demandez-en un nouveau à Apex.</p>
        <p className="mt-1 text-sm text-slate-500">Linkul nu este valabil. Cereți unul nou.</p>
      </Ecran>
    );
  }
  if (!data) return <Ecran><p className="text-slate-400">Chargement de la grille…</p></Ecran>;

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 pb-24">
      <header className="sticky top-0 z-20 bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-2xl mx-auto px-4 pt-3 pb-2">
          <div className="flex items-center justify-between gap-3">
            <Logo />
            <span className="text-xs font-semibold text-slate-500 text-right">Grille de prix · avis de {data.nom}</span>
          </div>
          <div className="mt-3 flex items-center gap-2">
            <input type="search" value={recherche} onChange={e => setRecherche(e.target.value)} placeholder="Chercher un poste… / Caută…"
                   className="flex-1 min-w-0 px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-orange-500" />
            <div className="flex bg-slate-100 rounded-lg p-0.5 shrink-0">
              <button type="button" onClick={() => setFiltre('tous')} className={`px-3 py-1.5 rounded-md text-xs font-semibold ${filtre === 'tous' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-500'}`}>Tous</button>
              <button type="button" onClick={() => setFiltre('avis')} className={`px-3 py-1.5 rounded-md text-xs font-semibold ${filtre === 'avis' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-500'}`}>Mes avis ({nbAvis})</button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4">
        <section className="mt-4 bg-white rounded-xl border border-slate-200 p-4 text-sm leading-relaxed">
          <p className="font-semibold text-slate-900">Ce sont les prix HT de la grille Apex, tels qu'ils sortent sur un devis (fourniture + pose).</p>
          <p className="mt-1 text-slate-600">Pour chaque poste : <b>−</b> si c'est trop cher, <b>+</b> si c'est trop bas. Un appui = 5 %. Le prix proposé s'affiche, tout s'enregistre tout seul. Une note est possible sous chaque ligne. Rien ne change dans la grille sans validation d'Hadrien.</p>
          <p className="mt-2 text-slate-500">Acestea sunt prețurile fără TVA din grila Apex (material + manoperă). <b>−</b> dacă e prea scump, <b>+</b> dacă e prea ieftin ; o apăsare = 5 %. Se salvează singur. Poți lăsa o notă sub fiecare linie.</p>
        </section>

        {lots.map(l => (
          <section key={l.cat} className="mt-5">
            <h2 className="sticky top-[108px] z-10 -mx-4 px-4 py-2 bg-slate-50/95 backdrop-blur text-[11px] font-semibold uppercase tracking-[0.06em] text-slate-500">
              {l.label} <span className="font-normal normal-case tracking-normal">· {l.items.length} poste{l.items.length > 1 ? 's' : ''}</span>
            </h2>
            <div className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-100">
              {l.items.map(it => <Ligne key={it.cle} it={it} pas={data.pas || 0.05} etat={etat[it.cle]} onDelta={(d) => modifier(it.cle, { delta: (it.avis?.delta || 0) + d })} onCommentaire={(c) => modifier(it.cle, { commentaire: c })} onReset={() => modifier(it.cle, { delta: 0 })} />)}
            </div>
          </section>
        ))}
        {!lots.length && <p className="mt-8 text-center text-sm text-slate-400">Aucun poste ne correspond.</p>}
      </main>

      <footer className="fixed bottom-0 inset-x-0 bg-slate-900 text-white px-4 py-2.5 text-xs flex items-center justify-between" style={{ paddingBottom: 'calc(0.625rem + env(safe-area-inset-bottom, 0px))' }}>
        <span><b className="text-orange-400">{nbAvis}</b> avis donné{nbAvis > 1 ? 's' : ''} · enregistrés automatiquement</span>
        <span className="text-slate-400">Apex Rénovation</span>
      </footer>
    </div>
  );
}

function Ligne({ it, pas, etat, onDelta, onCommentaire, onReset }) {
  const [note, setNote] = useState(false);
  const [texte, setTexte] = useState(it.avis?.commentaire || '');
  const delta = it.avis?.delta || 0;
  const prix = it.prix * (1 + delta * pas);
  const pct = Math.round(delta * pas * 100);
  const ton = delta > 0 ? 'text-emerald-700' : delta < 0 ? 'text-red-700' : 'text-slate-900';
  return (
    <div className="px-3 py-2.5">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium text-slate-800 leading-snug">{it.label}</div>
          <div className="text-[11px] text-slate-400 mt-0.5">
            Apex : <b className="text-slate-600">{fmt(it.prix)} €</b> / {UNITES[it.unit] || it.unit}
            {etat === 'enregistre' && <span className="ml-2 text-emerald-600 font-semibold">✓ enregistré</span>}
            {etat === 'erreur' && <span className="ml-2 text-red-600 font-semibold">non enregistré — réessayez</span>}
          </div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <button type="button" onClick={() => onDelta(-1)} aria-label="Trop cher : −5 %" className="w-10 h-10 rounded-lg border border-slate-300 bg-white text-xl font-bold text-red-700 active:bg-red-50">−</button>
          <button type="button" onClick={delta ? onReset : undefined} title={delta ? 'Revenir au prix Apex' : ''} className={`min-w-[4.5rem] h-10 rounded-lg text-sm font-bold tabular-nums ${delta ? 'bg-orange-50 border border-orange-200' : 'bg-slate-50 border border-slate-200'} ${ton}`}>
            {fmt(prix)} €{delta ? <span className="block text-[10px] font-semibold leading-none">{pct > 0 ? '+' : ''}{pct} %</span> : null}
          </button>
          <button type="button" onClick={() => onDelta(1)} aria-label="Trop bas : +5 %" className="w-10 h-10 rounded-lg border border-slate-300 bg-white text-xl font-bold text-emerald-700 active:bg-emerald-50">+</button>
        </div>
      </div>
      <div className="mt-1.5 flex items-center gap-3">
        <button type="button" onClick={() => setNote(n => !n)} className={`text-[11px] font-semibold ${it.avis?.commentaire ? 'text-orange-700' : 'text-slate-400'}`}>
          {it.avis?.commentaire ? 'Note ✎' : note ? 'Fermer' : 'Ajouter une note / notă'}
        </button>
      </div>
      {note && (
        <textarea value={texte} onChange={e => setTexte(e.target.value)} onBlur={() => onCommentaire(texte.trim())} rows={2} placeholder="Pourquoi ? Un prix réel, une fourniture, une astuce… / De ce ?"
                  className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500" />
      )}
    </div>
  );
}

function Ecran({ children }) {
  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-white rounded-2xl p-8 shadow-sm border border-slate-200 text-center"><div className="flex justify-center mb-6"><Logo /></div>{children}</div>
    </div>
  );
}
